import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { localApi, credentials } from "utils/imgApi";
import styles from "assets/jss/material-dashboard-pro-react/views/registerPageStyle";

const useStyles = makeStyles(styles);

const ALL_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "websitename", label: "Website Name" },
  { key: "typeId", label: "Type ID" },
  { key: "type", label: "Type" },
  { key: "profit", label: "Profit" },
  { key: "firstname", label: "First Name" },
  { key: "lastname", label: "Last Name" },
  { key: "mobile", label: "Mobile" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "email", label: "Email" },
  { key: "company", label: "Company" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "smsMarketing", label: "SMS Marketing" },
  { key: "openclosecrm", label: "Open/Close CRM" },
  { key: "createDate", label: "Create Date" },
  { key: "currentDateTime", label: "Current Date Time" },
  { key: "crmReferenceId", label: "CRM Reference ID" },
  { key: "countryCode", label: "Country Code" },
  { key: "isoNumber", label: "ISO Number" },
  { key: "url", label: "URL" },
  { key: "regAdminId", label: "Reg Admin ID" },
  { key: "lastDay", label: "Last Day" },
  { key: "active", label: "Active" },
];

export default function CsvFileImport() {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [selectedColumns, setSelectedColumns] = useState(
    ALL_COLUMNS.map((column) => column.key),
  );

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleColumn = (key) => {
    setSelectedColumns((previousColumns) =>
      previousColumns.includes(key)
        ? previousColumns.filter((columnKey) => columnKey !== key)
        : [...previousColumns, key],
    );
  };

  const handleSelectAll = () => {
    setSelectedColumns(ALL_COLUMNS.map((column) => column.key));
  };

  const handleClearAll = () => {
    setSelectedColumns([]);
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const refreshPageAfterDownload = () => {
    window.setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleDownload = async () => {
    if (selectedColumns.length === 0) {
      alert("Please select at least one column");
      return;
    }

    if (!fromDate || !toDate) {
      alert("Please select both start and end date");
      return;
    }

    if (fromDate > toDate) {
      alert("Start date must be before end date");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        columns: selectedColumns,
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
      };

      const response = await axios.post(`${localApi}/contact/import`, payload, {
        responseType: "blob",
        headers: {
          Authorization: `Basic ${credentials}`,
        },
      });

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `contacts_import_${new Date().getTime()}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      refreshPageAfterDownload();
    } catch (err) {
      console.error("Download failed", err);
      alert("Failed to download CSV");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={10} md={8}>
          <Card>
            <CardBody>
              <h4
                style={{
                  marginTop: 0,
                  marginBottom: 24,
                  fontWeight: 500,
                  color: "#333",
                }}
              >
                CSV File Import
              </h4>

              <h5
                style={{
                  marginBottom: 12,
                  fontWeight: 500,
                  color: "#555",
                }}
              >
                1. Select Columns
              </h5>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <span style={{ color: "#777", fontSize: 13 }}>
                  {selectedColumns.length} of {ALL_COLUMNS.length} Columns
                  Selected
                </span>

                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    color="info"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={loading}
                  >
                    Select All
                  </Button>

                  <Button
                    color="default"
                    size="sm"
                    onClick={handleClearAll}
                    disabled={loading}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <div
                style={{
                  maxHeight: 250,
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  padding: 12,
                  marginBottom: 24,
                  backgroundColor: "#fafafa",
                }}
              >
                {ALL_COLUMNS.map((column) => (
                  <label
                    key={column.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "7px 4px",
                      marginBottom: 2,
                      color: "#444",
                      fontSize: 14,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(column.key)}
                      onChange={() => toggleColumn(column.key)}
                      disabled={loading}
                      style={{
                        width: 16,
                        height: 16,
                        marginRight: 10,
                        cursor: "pointer",
                      }}
                    />

                    <span>
                      {column.label}{" "}
                      <span style={{ color: "#999" }}>({column.key})</span>
                    </span>
                  </label>
                ))}
              </div>

              <h5
                style={{
                  marginBottom: 12,
                  fontWeight: 500,
                  color: "#555",
                }}
              >
                2. Select Date Range{" "}
                <span style={{ color: "#888", fontSize: 13 }}>
                  (Current Date Time)
                </span>
              </h5>

              <div
                style={{
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    flex: "1 1 220px",
                    minWidth: 200,
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#555",
                      fontSize: 14,
                    }}
                  >
                    From
                  </label>

                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    maxDate={toDate || undefined}
                    disabled={loading}
                    className="date-picker-input"
                    wrapperClassName="date-picker-wrapper"
                  />
                </div>

                <div
                  style={{
                    flex: "1 1 220px",
                    minWidth: 200,
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#555",
                      fontSize: 14,
                    }}
                  >
                    To
                  </label>

                  <DatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    minDate={fromDate || undefined}
                    disabled={loading}
                    className="date-picker-input"
                    wrapperClassName="date-picker-wrapper"
                  />
                </div>
              </div>

              <div>
                <Button
                  color="primary"
                  onClick={handleDownload}
                  disabled={loading}
                >
                  {loading ? "Generating CSV..." : "Download CSV"}
                </Button>
              </div>

              <style>
                {`
                  .date-picker-wrapper {
                    display: block;
                    width: 100%;
                  }

                  .date-picker-input {
                    width: 100%;
                    height: 40px;
                    box-sizing: border-box;
                    padding: 8px 12px;
                    border: 1px solid #d2d2d2;
                    border-radius: 4px;
                    background: #fff;
                    color: #555;
                    font-size: 14px;
                    outline: none;
                  }

                  .date-picker-input:focus {
                    border-color: #9c27b0;
                    box-shadow: 0 0 0 1px #9c27b0;
                  }

                  .date-picker-input:disabled {
                    background: #f5f5f5;
                    cursor: not-allowed;
                  }
                `}
              </style>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
